import {Component, OnDestroy, OnInit} from "@angular/core";
import {Observable, of, Subject} from "rxjs";
import {debounceTime, finalize, switchMap, tap} from "rxjs/operators";
import {SubSink} from "subsink";
import {localizationKey, LocalizationStrings} from "../../i18n/i18n-model";
import {ErrorCode, ErrorResponse, singleCodeFromResponse} from "../../tools/error-response";
import {PropertyChangeListener} from "../../tools/form/form-field-config";
import {RouteNavigator} from "../../tools/navigation/route-navigator.service";
import {Notifications, NotificationType} from "../../tools/notifications/notifications";
import {MeasurementType} from "../measurement-models";
import {emptyEcgSymptom, MAX_ECG_SYMPTOMS, MeasurementExistsResponse, PatientData} from "./upload-model";
import {mapFormToData, mapFormToUploadInitiatedData} from "./upload-data-mapper";
import {UploadFormConfig} from "./upload-form-config";
import {emptyUploadFormModel, UploadFormModel} from "./upload-form-model";
import {UploadValidator} from "./upload-validator";
import {UploadService} from "./upload.service";
import {ActivatedRoute} from "@angular/router";
import {MeasurementIdProvider} from "../analize/measurement-id-provider";
import {HospitalMeasurementTypes} from "../hospitals/edit-hospital/hospital-measurement-types.service";
import {uploadFormMutuallyDependentProperties} from "./create-measurement-form-validation-map";
import {InitiatedMeasurementData, NurseMeasurementType} from "../initiate-measurement/initiate-measurement-data";
import {addEmptyEcgSymptoms} from "../utils";
import {getTypeSelectionOptions} from "../measurement-type-select-options";
import {ReportLanguageSettings} from "../hospitals/report-language-settings";
import {ExternalAnalysisType} from "../hospitals/edit-hospital/hospital.details";
import {getNurseMeasurementType} from "../get-nurse-measurement-type";
import {SelectionOptions} from "../../tools/form/form-field.component";
import {mapNurseMeasurementTypeToMeasurementType} from "./mapNurseMeasurementTypeToMeasurementType";
import {NavigationRoute} from "../../tools/navigation/navigation-route";
import {I18nService} from "../../i18n/i18n.service";
import {UploadActionHelper} from "./upload-action-helper";
import {DateTime} from "luxon";
import {replaceAnchors} from "../../tools/replace-anchors/replace-anchors";
import {ReleaseToggleState} from "../../tools/release-toggles/release-toggle-state";

export interface UploadContext {
  initiatedMeasurementData?: InitiatedMeasurementData;
  allowedMeasurementTypes: HospitalMeasurementTypes;
  languageSettings: ReportLanguageSettings;
  ecgTechnicianAnalysisOnlyEnabled: boolean;
  usesDateOfBirth: boolean;
  initiateTime?: string;
  externalAnalysisType: ExternalAnalysisType;
}

@Component({
  selector: "app-upload",
  templateUrl: "./upload.component.html",
  styleUrls: ["./upload.component.css"],
  standalone: false
})
export class UploadComponent implements PropertyChangeListener<UploadFormModel>, OnDestroy, OnInit {
  readonly uploadFormModel: UploadFormModel = emptyUploadFormModel();

  formConfig?: UploadFormConfig;

  uploadValidator!: UploadValidator;

  private errorMessages = new Map<ErrorCode, keyof LocalizationStrings>()
    .set(ErrorCode.CODE_NOT_UNIQUE, "uploadFailedCodeNotUnique")
    .set(ErrorCode.FILE_NAME_NOT_UNIQUE, "uploadFailedFileNameNotUnique");
  private readonly propertyChangeMeasurementCode = new Subject<string>();
  readonly propertyChangeMeasurementCode$: Observable<string> = this.propertyChangeMeasurementCode.asObservable();
  measurementCodeExists = false;
  private readonly codeCheckInProgress = new Subject<boolean>();
  readonly codeCheckInProgress$: Observable<boolean> = this.codeCheckInProgress.asObservable();
  private readonly uploadInProgress = new Subject<boolean>();
  readonly uploadInProgress$: Observable<boolean> = this.uploadInProgress.asObservable();

  private readonly subSink = new SubSink();
  errorMessage?: keyof LocalizationStrings;

  warningMessage: string | undefined = undefined;

  measurementId?: number;
  reportLanguageSelectionEnabled = false;
  showLoadingSpinner = true;
  showError = false;

  showForm = false;

  private hospitalLanguage?: string;

  private nurseDeviceSerial: string | undefined = undefined;
  private uploadActionHelper: UploadActionHelper | undefined = undefined;
  ecgTechnicianAnalysisOnlyEnabled = false;
  externalAnalysisType: ExternalAnalysisType = "CARDIOLOGS";

  constructor(
    private readonly uploadService: UploadService,
    private readonly notifications: Notifications,
    private readonly navigator: RouteNavigator,
    private readonly route: ActivatedRoute,
    private readonly measurementIdProvider: MeasurementIdProvider,
    private readonly i18nService: I18nService
  ) {
    this.subSink.add(
      this.propertyChangeMeasurementCode$.pipe(
        tap(() => this.codeCheckInProgress.next(true)),
        debounceTime(500),
        switchMap((measurementCode: string) => {
          if (measurementCode) {
            return this.uploadService.doesMeasurementExist(measurementCode);
          }

          return of({exists: false} as MeasurementExistsResponse);
        })
      ).subscribe({
        next: (response) => {
          this.codeCheckInProgress.next(false);
          this.measurementCodeExists = response.exists;
          this.errorMessage = this.measurementCodeExists ? "uploadErrorCodeNotUnique" : undefined;
        },
        error: (error) => {
          console.error(error);
          this.codeCheckInProgress.next(false);
        },
      })
    );
  }

  ngOnInit(): void {
    this.measurementId = this.measurementIdProvider.getId(this.route);

    this.uploadService.getUploadContext(this.measurementId)
      .pipe(
        finalize(() => this.showLoadingSpinner = false)
      )
      .subscribe(
        (uploadContext) => {
          this.initialiseUploadForm(uploadContext);
          this.showForm = true;
          this.nurseDeviceSerial = uploadContext.initiatedMeasurementData?.deviceSerial;
          this.ecgTechnicianAnalysisOnlyEnabled = uploadContext.ecgTechnicianAnalysisOnlyEnabled;
          this.externalAnalysisType = uploadContext.externalAnalysisType;
        },
        () => this.showError = true
      );
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  shouldShowFileUpload(): boolean {
    return this.isTypeSelected();
  }

  shouldShowForm(type: MeasurementType): boolean {
    return this.uploadFormModel.measurementType === type;
  }

  isTypeSelected(): boolean {
    return !!this.uploadFormModel.measurementType;
  }

  feature621Released(): boolean {
    return ReleaseToggleState.getInstance().isReleased("DEPRECATION_WARNING_FOR_OLD_UPLOAD_FLOW_621");
  }

  handlePropertyChange(property: keyof UploadFormModel, newValue: any): void {
    if (property === "measurementFileName") {
      this.warningMessage = undefined;
      this.uploadActionHelper = undefined;
    }
    if (property === "measurementType") {
      const measurementCode = this.uploadFormModel.measurementCode;
      Object.assign(this.uploadFormModel, emptyUploadFormModel());
      this.uploadFormModel.symptoms.length = 0;
      this.uploadFormModel.symptoms.push(...new Array(MAX_ECG_SYMPTOMS).fill(0).map((_) => emptyEcgSymptom()));
      this.uploadFormModel.reportLanguage = this.hospitalLanguage;
      this.uploadValidator.resetValidationStatuses();
      this.errorMessage = undefined;
      if (measurementCode !== "") {
        this.uploadFormModel.measurementCode = measurementCode;
        this.uploadValidator.updatePropertyValidation("measurementCode");
        this.propertyChangeMeasurementCode.next(measurementCode);
      }
      this.uploadActionHelper = undefined;
      this.warningMessage = undefined;
      this.formConfig!.updateHeightAndWeightMandatory(newValue);
    }
    (this.uploadFormModel as any)[property] = newValue;
    this.uploadValidator.updatePropertyValidation(property);

    if (uploadFormMutuallyDependentProperties.includes(property)) {
      uploadFormMutuallyDependentProperties.forEach((p) => this.uploadValidator.updatePropertyValidation(p));
    }

    if (property === "measurementCode") {
      this.propertyChangeMeasurementCode.next(newValue);
    }
  }

  uploadClick(): void {
    this.uploadValidator.updateAllValidations();

    const isValid = this.uploadValidator.isFormValid();

    if (!isValid) {
      this.errorMessage = "uploadFormValidationErrorDefault";
      return;
    }

    this.uploadInProgress.next(true);

    this.uploadActionHelper = this.uploadActionHelper || new UploadActionHelper(
      {
        measurementId: this.measurementId,
        nurseDeviceSerial: this.nurseDeviceSerial,
        uploadInitiated: () => this
          .uploadService
          .uploadInitiatedMeasurement(mapFormToUploadInitiatedData(this.uploadFormModel, this.measurementId!)),
        uploadNoninitiated: () => this.uploadService.upload(mapFormToData(this.uploadFormModel)),
        extractAndSaveDeviceSerial: () => this.uploadService.extractAndSaveDeviceSerial(this.uploadFormModel.measurementSessionId),
      }
    );

    this.uploadActionHelper.onUploadClick({
      onDeviceSerialsDoNotMatch: extractedDeviceSerial => this.warningMessage =
        replaceAnchors(this.i18nService.getLocalizedString("uploadFormWarningDeviceSerialMismatch"), {
          nurseDeviceSerial: this.nurseDeviceSerial,
          extractedDeviceSerial: extractedDeviceSerial.deviceSerial,
        }),
      onUploadSuccessful: () => {
        Object.assign(this.uploadFormModel, emptyUploadFormModel());
        this.notifications.addNotification(NotificationType.OK, localizationKey("uploadSucceeded"));
        this.navigator.navigateTo(NavigationRoute.LIST);
      },
      onActionFinished: () => this.uploadInProgress.next(false),
      onError: (errorResponse: ErrorResponse) => {
        this.errorMessage = this.errorMessages.get(singleCodeFromResponse(errorResponse)) || "uploadFailed";
      }
    });
  }

  private mapPatientDataToUploadFormModel(patientData: PatientData): void {
    Object.keys(patientData).forEach(property => {
      // @ts-ignore
      this.uploadFormModel[property] = patientData[property];
    });

  }

  private initialiseUploadForm(uploadContext: UploadContext) {

    const data = uploadContext.initiatedMeasurementData;

    if (data !== undefined) {
      this.uploadFormModel.measurementCode = data.code;
      this.uploadFormModel.measurementType = getNurseMeasurementType(
        data.type,
        data.isHome,
        {
          specifiedHolterDuration: data.specifiedDuration
        }
      );
      this.uploadFormModel.measurementStartTime = data.measurementStartTime;
      const patientData = addEmptyEcgSymptoms(data.patientData!);
      this.mapPatientDataToUploadFormModel(patientData);
    }

    const typeSelectionOptions: SelectionOptions<NurseMeasurementType> = getTypeSelectionOptions(
      data === undefined
        ? {
          types: uploadContext.allowedMeasurementTypes.types,
          homeTypes: []
        }
        : {
          types: data.isHome ? [] : [data.type],
          homeTypes: data.isHome ? [data.type] : []
        }
    );

    this.uploadValidator = new UploadValidator(
      this.uploadFormModel,
      {
        ecgTechnicianAnalysisOnlyEnabled: uploadContext.ecgTechnicianAnalysisOnlyEnabled,
        initiateTime: uploadContext.initiateTime,
        now: DateTime.now().toISO()
      }
    );

    this.formConfig = new UploadFormConfig(
      {
        model: this.uploadFormModel,
        propertyChangeListener: this,
        validationProvider: (property) => this.uploadValidator.isPropertyValid(property),
        uploadingInitiatedMeasurement: this.measurementId !== undefined,
        uploadingOrderedMeasurement: data ? data.isOrdered : false,
        allowedTypeOptions: typeSelectionOptions,
        editingMeasurement: false,
        isHomeEForm: false,
        usesDateOfBirth: uploadContext.usesDateOfBirth,
        reportLanguages: [
          uploadContext.languageSettings.language,
          ...uploadContext.languageSettings.allowedReportLanguages
        ]
      }
    );

    this.reportLanguageSelectionEnabled = uploadContext.languageSettings.reportLanguageSelectionEnabled;
    if (this.reportLanguageSelectionEnabled) {
      this.hospitalLanguage = uploadContext.languageSettings.language;
      this.uploadFormModel.reportLanguage = uploadContext.languageSettings.language;
    }
  }

  protected readonly localizationKey = localizationKey;


  getMeasurementType(nurseMeasurementType: NurseMeasurementType | undefined): MeasurementType | undefined {
    return mapNurseMeasurementTypeToMeasurementType(nurseMeasurementType);
  }

  getUploadButtonLabel(): keyof LocalizationStrings {
    return this.warningMessage ? "uploadFormConfirmButtonLabel" : "uploadFormButtonLabel";
  }

  setUploadSessionId(id: number) {
    this.uploadFormModel.measurementSessionId = id;
  }
}

